"use client"

import { useState, useRef, useEffect } from "react"
import { Utensils, Camera, Bed, ShoppingBag, Leaf, Plane, Coffee, Martini, Briefcase } from "lucide-react"

import "./TypeSelector.css"

const TypeSelector = ({ currentType, onTypeChange, place }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const selectorRef = useRef(null)

  const types = [
    { id: "restaurant", label: "Restaurant", icon: Utensils, color: "#ff6b35" },
    { id: "activity", label: "Activity", icon: Camera, color: "#4ecdc4" },
    { id: "lodging", label: "Lodging", icon: Bed, color: "#45b7d1" },
    { id: "nature", label: "Nature", icon: Leaf, color: "#4caf50" },
    { id: "airport", label: "Airport", icon: Plane, color: "#9c27b0" },
    { id: "coffee", label: "Coffee", icon: Coffee, color: "#ff9800" },
    { id: "drinks", label: "Drinks", icon: Martini, color: "#e91e63" },
    { id: "shopping", label: "Shopping", icon: ShoppingBag, color: "#ff4081" },
    { id: "luggage", label: "Luggage", icon: Briefcase, color: "#00bdff" },
  ]

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setIsExpanded(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleTypeSelect = (typeId) => {
    // Save to localStorage for persistence
    const savedTypes = JSON.parse(localStorage.getItem("triptoi-place-types") || "{}")
    const key = `${place.lat},${place.lng}`
    savedTypes[key] = typeId
    localStorage.setItem("triptoi-place-types", JSON.stringify(savedTypes))

    onTypeChange(typeId)
    setIsExpanded(false)
  }

  // Find current type data
  const currentTypeData = currentType ? types.find((t) => t.id === currentType) : null

  // If no type is selected OR type data not found, show the "Type >" button
  if (!currentType || !currentTypeData) {
    return (
      <div className="type-selector-inline-container" ref={selectorRef}>
        <button className="clean-filter-button" onClick={() => setIsExpanded(!isExpanded)} title="Select type">
          <span className="filter-text">Type ›</span>
        </button>

        {isExpanded && (
          <>
            <div className="type-selector-backdrop" onClick={() => setIsExpanded(false)} />
            <div className="type-horizontal-overlay">
              <div className="type-overlay-content">
                {types.map((type) => (
                  <button
                    key={type.id}
                    className="type-overlay-option"
                    onClick={() => handleTypeSelect(type.id)}
                    title={type.label}
                    aria-label={type.label}
                    style={{ backgroundColor: type.color }}
                  >
                    <type.icon size={20} style={{ color: "white" }} />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  // If a valid type is selected, show that type's button
  return (
    <div className="type-selector-inline-container" ref={selectorRef}>
      <button
        className="clean-filter-button type-selected-style"
        onClick={() => setIsExpanded(!isExpanded)}
        title={`Type: ${currentTypeData.label} (click to change)`}
        style={{ backgroundColor: currentTypeData.color }}
      >
        <currentTypeData.icon size={16} style={{ color: "white" }} />
      </button>

      {isExpanded && (
        <>
          <div className="type-selector-backdrop" onClick={() => setIsExpanded(false)} />
          <div className="type-horizontal-overlay">
            <div className="type-overlay-content">
              {types.map((type) => (
                <button
                  key={type.id}
                  className={`type-overlay-option ${currentType === type.id ? "active" : ""}`}
                  onClick={() => handleTypeSelect(type.id)}
                  title={type.label}
                  aria-label={type.label}
                  style={{ backgroundColor: type.color }}
                >
                  <type.icon size={20} style={{ color: "white" }} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TypeSelector
